import './Card.css'

/**
 * Surface used for page panels — and the ONE panel head every page uses, so a page
 * never re-invents where a panel's title, scope and buttons go.
 *
 * @param {string} [title]     optional heading shown in the card header
 * @param {string} [subtitle]  the scope the panel is drawn over ("لكل الفترات"),
 *                             which a chart card needs so a rate is never read
 *                             against the wrong period
 * @param {import('react').ElementType} [icon]  a soft identity tile before the title —
 *   one per panel head at most, for wayfinding on a page of several panels
 * @param {import('react').ReactNode} [actions]  the panel's own controls, at the head's
 *   far end: secondary buttons, a menu, a segmented switch
 * @param {ReactNode} [footer] optional action bar pinned to the card bottom
 * @param {boolean} [flat]     drop the border, radius and shadow when the card is
 *                             embedded in a surface that already provides them
 * @param {boolean} [compact]  16px instead of 20px, for a card in a tight grid
 * @param {boolean} [interactive]  the whole card is one target (the caller wraps it in
 *   the link or handles the click): the edge darkens on approach
 * @param {boolean} [selected]  the chosen one of a set of cards — the identity edge
 */
export default function Card({
  title,
  subtitle,
  icon: Icon,
  actions,
  footer,
  flat = false,
  compact = false,
  interactive = false,
  selected = false,
  className = '',
  children,
}) {
  const hasHead = Boolean(title || actions)
  return (
    <section
      className={[
        'card',
        flat && 'card--flat',
        compact && 'card--compact',
        interactive && 'card--interactive',
        selected && 'is-selected',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {hasHead && (
        <header className="card__header">
          {Icon && (
            <span className="card__icon" aria-hidden="true">
              <Icon size={18} strokeWidth={1.75} />
            </span>
          )}
          <div className="card__titles">
            {title && (
              <h3 className="card__title">
                <bdi>{title}</bdi>
              </h3>
            )}
            {subtitle && (
              <p className="card__subtitle">
                <bdi>{subtitle}</bdi>
              </p>
            )}
          </div>
          {actions && <div className="card__actions">{actions}</div>}
        </header>
      )}
      {children && <div className="card__body">{children}</div>}
      {footer && <footer className="card__footer">{footer}</footer>}
    </section>
  )
}
